import React from 'react';
import {makeStyles, createStyles, useTheme} from '@material-ui/core/styles';
import {
    useMediaQuery,
    Typography,
    IconButton,
    CircularProgress,
} from '@material-ui/core';
import Pick from '../Pick/pick';
import PickLimited from '../Pick/pickLimited';
import FavoriteIcon from '@material-ui/icons/Favorite';
import {postUnfavorite, postFavorite} from '../../actions/account';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            padding: `3em 0 0`,
        },
        card: {
            backgroundColor: 'white',
            borderRadius: '2.4em',
            border: `solid 1px ${theme.palette.grey.light}`,
            // maxWidth: '32em',
            position: 'relative',
            boxShadow: '0 0.1em 2em rgba(0,0,0,0.15)',
            backgroundPosition: 'center center',
            width: '100%',
            backgroundSize: 'cover',
            transition: 'border-color 150ms, box-shadow 150ms',
            [theme.breakpoints.up(`sm`)]: {
                // maxWidth: '400px',
            },
            '&:before': {
                content: '""',
                display: 'block',
                paddingBottom: '63%',
            },
        },
        cardHover: {
            '&:hover': {
                cursor: 'pointer',
                boxShadow: '0 0.1em 2em rgba(0,0,0,0.45)',
            },
        },
        cardInner: {
            position: 'absolute',
            left: 0,
            height: '100%',
            top: 0,
            width: '100%',
        },
        cardMerchantNameContainer: {
            position: 'absolute',
            bottom: 0,
            width: '100%',
            textAlign: 'center',
            padding: `0.4em 2em 1.4em`,
        },
        cardMerchantName: {
            fontSize: '2.2em',
            lineHeight: '1.2em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            [theme.breakpoints.up(`sm`)]: {
                fontSize: '2em',
            },
        },
        cardSuffix: {
            marginTop: '0.8em',
            fontSize: '2.4em',
        },
        pickContainer: {
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -3em)',
            width: '24em',
            height: '100%',
            maxWidth: '310px',
        },
        favoriteWrapper: {
            position: 'absolute',
            zIndex: 1,
            top: '0.5em',
            right: '0.5em',
            transform: 'translate(50%, -50%)',
        },
        favoriteIconButton: {
            display: 'block',
            position: 'relative',
            color: 'white',
            fontSize: '3em',
            padding: '0.5em',
            '& svg': {
                stroke: theme.palette.grey.medium,
                fontSize: '1.4em',
            },
        },
        favoriteIconButtonActive: {
            '& svg': {
                fill: theme.palette.primary.main,
                stroke: theme.palette.primary.main,
            },
        },
        loading: {
            position: 'absolute',
            left: '-1px',
            top: '-1px',
            width: '7.5em',
            height: '7.5em',
            '& circle': {
                strokeWidth: 2,
            },
        },
        cardHidden: {
            visibility: 'hidden',
        },
    })
);
const GiftCard = ({
    merchant,
    image,
    video,
    wrappingImage = null,
    title,
    tease = false, 
    favorite = false,
    showFavoriteIcon = true,
    swipeToOpen = true,
    cardStyles = {},
    gift,
    limited = false,
    children = null,
    pickId = null,
    id = '',
    sendUnwrapEvent = false,
    onClick = () => {},
    displayHover = false,
    suffix = null,
}) => {
    const postUnfavoriteError = useSelector(
        (state) => state.account.postUnfavoriteError
    );
    const postFavoriteError = useSelector(
        (state) => state.account.postFavoriteError
    );

    const ref = React.useRef();
    const classes = useStyles();
    const theme = useTheme();
    const dispatch = useDispatch();
    const xs = useMediaQuery(theme.breakpoints.only('xs'));
    const merchantNameVariant = xs ? 'body2' : 'body1';
    const [fontSize, setFontSize] = React.useState(10);
    const [resizeReady, setResizeReady] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const favoriteClasses = [
        classes.favoriteIconButton,
        favorite ? classes.favoriteIconButtonActive : '',
    ].join(' ');

    const handleFavoriteClick = () => {
        setLoading(true);
        if (favorite) {
            dispatch(postUnfavorite({id: merchant.id}));
        }
        if (!favorite) {
            dispatch(postFavorite({id: merchant.id}));
        }
    };

    React.useEffect(() => {
        const resize = () => {
            const width = ref.current.offsetWidth;
            if (width > 0) {
                clearInterval(interval);
            }
            setFontSize(Math.max(Math.floor(width / 37), 4.0));
            setResizeReady(true);
        };
        const interval = setInterval(resize, 400);
        window.addEventListener('resize', resize);
        return () => {
            window.removeEventListener('resize', resize);
            clearInterval(interval);
        };
    }, []);

    React.useEffect(() => {
        if (postUnfavoriteError || postFavoriteError) {
            setLoading(false);
        }
    }, [postUnfavoriteError, postFavoriteError]);

    React.useEffect(() => {
        setLoading(false);
    }, [favorite]);

    return (
        <div
            data-gift-card=""
            id={id}
            className={classes.root}
            ref={ref}
            style={{fontSize}}>
            <div
                style={cardStyles}
                className={`${classes.card} ${
                    displayHover ? classes.cardHover : ''
                } ${!resizeReady ? classes.cardHidden : ''}`}>
                <div className={classes.cardInner} onClick={onClick}>
                    {showFavoriteIcon && (
                        <div className={classes.favoriteWrapper}>
                            {favorite !== null && (
                                <>
                                    <IconButton
                                        className={favoriteClasses}
                                        color="primary"
                                        onClick={handleFavoriteClick}>
                                        <FavoriteIcon />
                                    </IconButton>
                                    {loading && (
                                        <CircularProgress
                                            size="35"
                                            className={classes.loading}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/*{!!gift?.aggregate_merchant && (*/}
                    {/*    <Box*/}
                    {/*        pt="1.4em"*/}
                    {/*        pl="1em"*/}
                    {/*        component="span"*/}
                    {/*        display="inline-block">*/}
                    {/*        <svg height="3.5em" viewBox="0 0 221.81 300.74">*/}
                    {/*            <path*/}
                    {/*                d="M110.91,0C49.66,0,0,49.64,0,110.89c0,22.64,6.8,43.68,18.44,61.23l78.51,121.04 c3.28,5.06,8.62,7.58,13.96,7.58c5.34,0,10.68-2.53,13.96-7.58l78.51-121.04c11.64-17.55,18.44-38.59,18.44-61.23 C221.81,49.64,172.16,0,110.91,0z M110.91,183c-39.81,0-72.09-32.28-72.09-72.09c0-39.81,32.28-72.09,72.09-72.09 c39.81,0,72.09,32.28,72.09,72.09C183,150.72,150.72,183,110.91,183z"*/}
                    {/*                fill="#3A4562"*/}
                    {/*            />*/}
                    {/*            <path*/}
                    {/*                d="M156.09,114.39c-4.9-1.42-10.03,1.41-11.45,6.31c-4.32,14.91-18.19,25.32-33.74,25.32 c-15.55,0-29.42-10.41-33.74-25.33c-1.42-4.9-6.55-7.73-11.45-6.3c-4.9,1.42-7.72,6.55-6.3,11.45c6.6,22.77,27.78,38.67,51.5,38.67 c23.73,0,44.9-15.9,51.49-38.68C163.82,120.93,160.99,115.81,156.09,114.39z"*/}
                    {/*                fill="#3A4562"*/}
                    {/*            />*/}
                    {/*        </svg>*/}
                    {/*    </Box>*/}
                    {/*)}*/}

                    {children && <>{children}</>}

                    <div
                        data-gift-card-pick-container=""
                        className={classes.pickContainer}>
                        {limited && image && <PickLimited image={image} />}
                        {!limited && image && (
                            <Pick
                                gift={gift}
                                sendUnwrapEvent={sendUnwrapEvent}
                                pickImage={image}
                                pickWrapper={wrappingImage}
                                tease={tease}
                                // autoOpen={autoOpen}
                                autoOpenDelay={5000}
                                swipeToOpen={swipeToOpen}
                                autoplayPreview={true}
                                pickId={pickId}
                                video={video ? video : null}
                                mediaType={video ? 'video' : null}
                                style={{maxWidth: '100px'}}
                            />
                        )}
                    </div>
                    <div className={classes.cardMerchantNameContainer}>
                        {title && (
                            <Typography
                                data-gift-card-title={title}
                                className={`${classes.cardMerchantName} gift-card-title`}
                                align="left"
                                variant={merchantNameVariant}
                                component="p">
                               <div dangerouslySetInnerHTML={{__html:title}}/>
                            </Typography>
                        )}
                    </div>
                </div>
            </div>
            {suffix && (
                <Typography
                    align="center"
                    className={classes.cardSuffix}
                    variant="body1"
                    component="p">
                    {suffix}
                </Typography>
            )}
        </div>
    );
};

GiftCard.propTypes = {
    merchantId: PropTypes.number,
    image: PropTypes.string,
    wrappingImage: PropTypes.string,
    title: PropTypes.string,
    id: PropTypes.string,
    favorite: PropTypes.bool,
    limited: PropTypes.bool,
    onClick: PropTypes.func,
    displayHover: PropTypes.bool,
    swipeToOpen: PropTypes.bool,
    suffix: PropTypes.string,
    showFavoriteIcon: PropTypes.bool,
    cardStyles: PropTypes.object,
    gift: PropTypes.object.isRequired,
    sendUnwrapEvent: PropTypes.bool,
    pickId: PropTypes.string,
};

GiftCard.defaultProps = {
    merchantId: null,
    image: null,
    wrappingImage: null,
    id: '',
    suffix: null,
    cardStyles: {},
    showFavoriteIcon: false,
    sendUnwrapEvent: false,
    pickId: null,
};

export default React.memo(GiftCard);